import { useState } from 'react';
import { EllipsisOutlined } from '@ant-design/icons';
import { Button, List } from 'antd';
import { useLiveQuery } from 'dexie-react-hooks';
import { useTranslation } from 'translations/hooks';
import { db, type Segment } from 'technical/db';
import { SynchronizationTag } from 'business/tunnel/pages/ring-scan/components/synchronization-tag';
import { DeleteSegmentDrawer } from 'business/tunnel/pages/ring-scan/components/delete-segment-drawer';

interface Props {
  ringId: string | null;
  ringNumber: number | null;
  onAfterDeleteClose?: () => void;
}

export const SegmentList = ({
  ringId,
  ringNumber,
  onAfterDeleteClose,
}: Props) => {
  const { t } = useTranslation();
  const [selectedSegment, setSelectedSegment] = useState<Segment | null>(null);

  const segments = useLiveQuery(() => {
    if (!ringId || !ringNumber) {
      return [];
    }
    if (ringId.startsWith('virtualRing')) {
      return db.segments
        .where('virtualRingNumber')
        .equals(ringNumber)
        .sortBy('segmentType');
    }

    return db.segments.where('ringId').equals(ringId).sortBy('segmentType');
  }, [ringId]);

  return (
    <>
      <p className="text-primary text-center text-base font-semibold">
        {t('tunnel.segmentList.title')}
      </p>
      <List
        dataSource={segments ?? []}
        locale={{ emptyText: t('tunnel.segmentList.empty') }}
        renderItem={(segment) => (
          <List.Item className="py-2">
            <div className="grid w-full grid-cols-[1fr_2.5rem_auto_auto] items-center gap-2">
              <span className="truncate font-medium">
                {segment.serialNumber}
              </span>
              <span className="truncate text-gray-500">
                {segment.segmentType}
              </span>
              <div className="min-w-30">
                <SynchronizationTag
                  isSynced={segment.desynchronizedAt === null}
                />
              </div>
              <Button
                type="text"
                icon={<EllipsisOutlined />}
                onClick={() => setSelectedSegment(segment)}
              />
            </div>
          </List.Item>
        )}
      />
      <DeleteSegmentDrawer
        segment={selectedSegment}
        onClose={() => {
          setSelectedSegment(null);
          onAfterDeleteClose?.();
        }}
      />
    </>
  );
};
